/**
 * 10.5M — TERRITÓRIOS: o mapa deixa de ser uma arena única e vira uma GRADE de pedaços,
 * cada um com nome, tier, bioma (visual) e GANGUE própria. Esta classe é a fonte de verdade
 * da geometria dos territórios da região ativa (centro, tamanho, quem está em qual pedaço).
 * Sem estado de jogo aqui — spawn é do EnemySpawner, visual é do Arena, UI é do mapa/minimapa.
 */
import { GameConfig, type BiomeDef, type TerritoryDef, type WorldRegionDef } from "../config/GameConfig";
import { randRange } from "../utils/math";

export interface Territory {
  def: TerritoryDef;
  cx: number; // centro X
  cz: number; // centro Z
  halfW: number;
  halfH: number;
  biome: BiomeDef;
}

const FALLBACK_BIOME: BiomeDef = {
  ground: 0x5f9c46, trunk: 0x3a2a1c, foliage: [0x27492e], rock: 0x3b3f4a, crystal: 0x7760d0,
  trees: 12, rocks: 6, grass: 12, crystals: 2,
};

/**
 * 10.6H — TAMANHO PRÓPRIO POR ÁREA. `arenaHalfSize` já existia na config de cada região mas
 * era ignorado: as três usavam o mesmo mapa de 210×210. Agora cada área tem o seu (a 1 é
 * compacta, a 3 é a maior), e é daqui que o Arena, o mapa e a muralha leem.
 */
export function regionHalfSize(regionId: string): number {
  const def = GameConfig.world.regions.find((r) => r.id === regionId) as WorldRegionDef | undefined;
  return def?.arenaHalfSize ?? GameConfig.arena.halfSize;
}

/**
 * 10.6O — grade com pesos irregulares. Calcula as bordas acumuladas de cada coluna/linha
 * a partir de colWeights/rowWeights (normalizado: a soma dos pesos se encaixa na largura total).
 */
function buildEdges(count: number, total: number, weights?: number[]): number[] {
  const w = weights && weights.length === count ? weights : Array.from({ length: count }, () => 1);
  const sum = w.reduce((a, b) => a + b, 0);
  const edges: number[] = [0];
  let acc = 0;
  for (let i = 0; i < count; i++) {
    acc += (w[i] / sum) * total;
    edges.push(acc);
  }
  return edges;
}

/** Geometria dos territórios de uma região (pura — usada pelo Arena e pelo manager). */
export function territoriesForRegion(regionId: string): Territory[] {
  const half = regionHalfSize(regionId);
  const def = GameConfig.world.regions.find((r) => r.id === regionId) as WorldRegionDef | undefined;
  const cols = Math.max(1, def?.grid?.cols ?? 3);
  const rows = Math.max(1, def?.grid?.rows ?? 3);
  const total = half * 2;
  const colEdges = buildEdges(cols, total, def?.grid?.colWeights);
  const rowEdges = buildEdges(rows, total, def?.grid?.rowWeights);
  return GameConfig.territories.list
    .filter((t) => t.regionId === regionId)
    .map((t) => {
      const x0 = -half + colEdges[t.col];
      const x1 = -half + colEdges[t.col + 1];
      const z0 = -half + rowEdges[t.row];
      const z1 = -half + rowEdges[t.row + 1];
      return {
        def: t,
        cx: (x0 + x1) / 2,
        cz: (z0 + z1) / 2,
        halfW: (x1 - x0) / 2,
        halfH: (z1 - z0) / 2,
        biome: (GameConfig.territories.biomes as Record<string, BiomeDef>)[t.biome] ?? FALLBACK_BIOME,
      };
    });
}

export class TerritoryManager {
  private list: Territory[] = [];
  private regionId = "";
  private half: number = GameConfig.arena.halfSize;

  constructor() {
    this.setRegion(GameConfig.world.regions[0] as WorldRegionDef);
  }

  /** Recalcula a grade para a região informada. */
  setRegion(def: WorldRegionDef): void {
    this.regionId = def.id;
    this.half = regionHalfSize(def.id);
    this.list = territoriesForRegion(def.id);
  }

  get region(): string {
    return this.regionId;
  }
  get all(): readonly Territory[] {
    return this.list;
  }
  get bound(): number {
    return this.half;
  }

  byId(id: string): Territory | null {
    return this.list.find((t) => t.def.id === id) ?? null;
  }

  /** Território que contém (x,z) — busca por bounding box (funciona com grade irregular). */
  at(x: number, z: number): Territory | null {
    for (const t of this.list) {
      if (Math.abs(x - t.cx) <= t.halfW && Math.abs(z - t.cz) <= t.halfH) return t;
    }
    return null;
  }

  /** Territórios cujo centro está a até `radius` do ponto (spawn ativo). */
  activeAround(x: number, z: number, radius: number, out: Territory[]): Territory[] {
    out.length = 0;
    const r2 = radius * radius;
    for (const t of this.list) {
      const dx = t.cx - x;
      const dz = t.cz - z;
      if (dx * dx + dz * dz <= r2) out.push(t);
    }
    return out;
  }

  /** Ponto aleatório dentro do território (com margem). */
  randomPointIn(t: Territory, margin = 3): { x: number; z: number } {
    const mw = Math.max(0, t.halfW - margin);
    const mh = Math.max(0, t.halfH - margin);
    return { x: t.cx + randRange(-mw, mw), z: t.cz + randRange(-mh, mh) };
  }
}
